/**
 * Payroll **Manage** — the salary-component template, `/payroll`, WP-W9.
 * `web/shapes/payroll.md` §6 is the agreed shape.
 *
 * Rendered only for a session holding `payroll.manage_templates`
 * (`PayrollScreen` decides that, the same client-side honesty gate `LeaveScreen`'s
 * approvals section and `PerformanceScreen`'s Manage section already use — never the
 * authorisation, which the server still runs on every call). Unlike the leave and
 * performance surfaces, `authorizer.ts`'s `createSessionTemplateViewAuthorizer` uses
 * the SAME code as `createSessionTemplateManageAuthorizer` — there is no separate
 * "view" code for the template, so this whole panel is gated on one code.
 *
 * Two subsections: the company's templates (list + create), and — for a template
 * selected from that list — its component defs (list + add one). A template's `id`
 * is already in hand from the list this panel just read, so choosing one is a button
 * on the row rather than a typed id (unlike the period ids elsewhere in this module,
 * which stay typed because the reading session may not hold the permission their
 * own directory endpoint requires — `MyPayslipPanel.tsx`'s own header explains why).
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  PayrollComponentDef,
  PayrollComponentDefCreateRequest,
  PayrollComponentKind,
  PayrollComponentSource,
  PayrollTemplate,
  PayrollTemplateCreateRequest,
} from '../../api/payroll';
import { PAYROLL_COMPONENT_KINDS, PAYROLL_COMPONENT_SOURCES } from '../../api/payroll';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { SEVERITY_ICON } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

const NAME_MAX = 300;
const KEY_MAX = 100;

export interface TemplateManagePanelProps {
  readonly templatesStatus: QueryStatus;
  readonly templates: readonly PayrollTemplate[] | null;
  readonly templatesError: WorkforceApiError | null;
  readonly onReloadTemplates: () => void;
  readonly templateCreatePending: boolean;
  readonly templateCreateError: WorkforceApiError | null;
  readonly templateCreated: PayrollTemplate | null;
  readonly onCreateTemplate: (body: PayrollTemplateCreateRequest) => void;

  readonly selectedTemplateId: string | null;
  readonly onSelectTemplate: (id: string) => void;
  readonly defsStatus: QueryStatus;
  readonly defs: readonly PayrollComponentDef[] | null;
  readonly defsError: WorkforceApiError | null;
  readonly onReloadDefs: () => void;
  readonly defCreatePending: boolean;
  readonly defCreateError: WorkforceApiError | null;
  readonly defCreated: PayrollComponentDef | null;
  readonly onCreateDef: (body: PayrollComponentDefCreateRequest) => void;
}

export function TemplateManagePanel(props: TemplateManagePanelProps): ReactNode {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [seedDefaults, setSeedDefaults] = useState(true);

  const [defKey, setDefKey] = useState('');
  const [defName, setDefName] = useState('');
  const [defKind, setDefKind] = useState<PayrollComponentKind>('earning');
  const [defSource, setDefSource] = useState<PayrollComponentSource>('constant');
  const [defFormula, setDefFormula] = useState('');
  const [defSortOrder, setDefSortOrder] = useState('');

  const nameReady = name.trim().length > 0;
  const defReady = defKey.trim().length > 0 && defName.trim().length > 0;

  return (
    <section className="wp-section" aria-labelledby="w9-manage-heading" data-testid="w9-manage">
      <h2 className="wp-section__title" id="w9-manage-heading">
        {t('payroll.manage.title')}
      </h2>
      <p className="wp-small wp-muted">{t('payroll.manage.intro')}</p>

      {/* ── Templates ───────────────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w9-templates">
        <h3 className="wp-section__title">{t('payroll.templates.title')}</h3>

        <form
          className="wp-filters wp-filters--bar"
          onSubmit={(event) => {
            event.preventDefault();
            if (!nameReady || props.templateCreatePending) return;
            props.onCreateTemplate({
              name: name.trim(),
              is_active: isActive,
              seed_achau_defaults: seedDefaults,
            });
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w9-template-name">
              {t('payroll.templates.create.name')}
            </label>
            <input
              className="wp-field__input"
              id="w9-template-name"
              data-testid="w9-template-name"
              type="text"
              value={name}
              maxLength={NAME_MAX}
              autoComplete="off"
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w9-template-active">
              <input
                id="w9-template-active"
                data-testid="w9-template-active"
                type="checkbox"
                checked={isActive}
                onChange={(event) => {
                  setIsActive(event.target.checked);
                }}
              />{' '}
              {t('payroll.templates.create.active')}
            </label>
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w9-template-seed">
              <input
                id="w9-template-seed"
                data-testid="w9-template-seed"
                type="checkbox"
                checked={seedDefaults}
                onChange={(event) => {
                  setSeedDefaults(event.target.checked);
                }}
              />{' '}
              {t('payroll.templates.create.seedDefaults')}
            </label>
            <p className="wp-small wp-muted">{t('payroll.templates.create.seedDefaultsHint')}</p>
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--primary"
              type="submit"
              data-testid="w9-template-submit"
              disabled={!nameReady || props.templateCreatePending}
            >
              {props.templateCreatePending
                ? t('payroll.templates.create.submitting')
                : t('payroll.templates.create.submit')}
            </button>
          </div>
        </form>
        {props.templateCreateError === null ? null : (
          <ApiErrorNote
            error={props.templateCreateError}
            subject={t('payroll.templates.subject')}
            testId="w9-template-create-error"
          />
        )}
        {props.templateCreated === null ? null : (
          <StatusNote
            severity="low"
            title={t('payroll.templates.create.ok', { name: props.templateCreated.name })}
            testId="w9-template-create-ok"
          />
        )}

        {props.templatesStatus === 'loading' ? (
          <Skeleton
            rows={2}
            variant="block"
            label={t('payroll.templates.subject')}
            testId="w9-templates-skeleton"
          />
        ) : null}
        {props.templatesStatus === 'error' && props.templatesError !== null ? (
          <ApiErrorNote
            error={props.templatesError}
            onRetry={props.onReloadTemplates}
            subject={t('payroll.templates.subject')}
            testId="w9-templates-error"
          />
        ) : null}
        {props.templates === null ? null : props.templates.length === 0 ? (
          <EmptyState
            title={t('payroll.templates.empty.title')}
            description={t('payroll.templates.empty.body')}
            testId="w9-templates-empty"
          />
        ) : (
          <ul className="wp-rows" data-testid="w9-templates-list">
            {props.templates.map((template) => {
              const severity = template.is_active ? 'low' : 'medium';
              return (
                <li className="wp-row" key={template.id} data-testid={`w9-template-${template.id}`}>
                  <div className="wp-row__main">
                    <p>
                      <Badge
                        severity={severity}
                        icon={SEVERITY_ICON[severity]}
                        label={t(
                          template.is_active
                            ? 'payroll.templates.active'
                            : 'payroll.templates.inactive',
                        )}
                        testId={`w9-template-active-${template.id}`}
                      />{' '}
                      <span>{template.name}</span>
                    </p>
                  </div>
                  <div className="wp-row__figure">
                    <button
                      className="wp-button"
                      type="button"
                      data-testid={`w9-template-select-${template.id}`}
                      onClick={() => {
                        props.onSelectTemplate(template.id);
                      }}
                    >
                      {t('payroll.templates.viewComponents')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Component defs of the selected template ────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w9-defs">
        <h3 className="wp-section__title">{t('payroll.defs.title')}</h3>

        {props.selectedTemplateId === null ? (
          <EmptyState
            title={t('payroll.defs.none.title')}
            description={t('payroll.defs.none.body')}
            testId="w9-defs-none"
          />
        ) : (
          <>
            {props.defsStatus === 'loading' ? (
              <Skeleton
                rows={2}
                variant="block"
                label={t('payroll.defs.subject')}
                testId="w9-defs-skeleton"
              />
            ) : null}
            {props.defsStatus === 'error' && props.defsError !== null ? (
              <ApiErrorNote
                error={props.defsError}
                onRetry={props.onReloadDefs}
                subject={t('payroll.defs.subject')}
                testId="w9-defs-error"
              />
            ) : null}
            {props.defs === null ? null : props.defs.length === 0 ? (
              <EmptyState
                title={t('payroll.defs.empty.title')}
                description={t('payroll.defs.empty.body')}
                testId="w9-defs-empty"
              />
            ) : (
              <ul className="wp-rows" data-testid="w9-defs-list">
                {props.defs.map((def) => (
                  <li className="wp-row" key={def.id} data-testid={`w9-def-${def.id}`}>
                    <div className="wp-row__main">
                      <p>
                        <span>{def.name}</span> · <span className="wp-small">{def.key}</span>
                      </p>
                      <p className="wp-small wp-muted">
                        {t('payroll.defs.row.meta', { kind: def.kind, source: def.source })}
                      </p>
                      {/*
                        The unconfirmed marker (migration 051). Present ONLY when the
                        server sent one: a seeded statutory row carries it until the
                        accountant signs that row off, and a hand-created component
                        carries `null`. Rendered as the row-level `Badge` — icon AND
                        words, never colour alone (DESIGN.md §4) — with the server's own
                        text beside it, because the note says *what* is unconfirmed and
                        a fixed label could not. `medium`, not `high`: an unconfirmed
                        number is a thing to check before the first real run, not a
                        failure of anything the person just did.
                      */}
                      {def.confirmation_note === null ? null : (
                        <p className="wp-small">
                          <Badge
                            severity="medium"
                            icon={SEVERITY_ICON.medium}
                            label={t('payroll.defs.row.unconfirmed')}
                            testId={`w9-def-unconfirmed-${def.id}`}
                          />{' '}
                          <span>{def.confirmation_note}</span>
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <form
              className="wp-filters wp-filters--bar"
              onSubmit={(event) => {
                event.preventDefault();
                if (!defReady || props.defCreatePending) return;
                const trimmedFormula = defFormula.trim();
                const trimmedSort = defSortOrder.trim();
                props.onCreateDef({
                  key: defKey.trim(),
                  name: defName.trim(),
                  kind: defKind,
                  source: defSource,
                  ...(trimmedFormula === '' ? {} : { formula_expression: trimmedFormula }),
                  ...(trimmedSort === '' ? {} : { sort_order: Number(trimmedSort) }),
                });
              }}
            >
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w9-def-key">
                  {t('payroll.defs.create.key')}
                </label>
                <input
                  className="wp-field__input"
                  id="w9-def-key"
                  data-testid="w9-def-key"
                  type="text"
                  value={defKey}
                  maxLength={KEY_MAX}
                  autoComplete="off"
                  onChange={(event) => {
                    setDefKey(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w9-def-name">
                  {t('payroll.defs.create.name')}
                </label>
                <input
                  className="wp-field__input"
                  id="w9-def-name"
                  data-testid="w9-def-name"
                  type="text"
                  value={defName}
                  maxLength={NAME_MAX}
                  autoComplete="off"
                  onChange={(event) => {
                    setDefName(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w9-def-kind">
                  {t('payroll.defs.create.kind')}
                </label>
                <select
                  className="wp-field__input"
                  id="w9-def-kind"
                  data-testid="w9-def-kind"
                  value={defKind}
                  onChange={(event) => {
                    setDefKind(event.target.value as PayrollComponentKind);
                  }}
                >
                  {PAYROLL_COMPONENT_KINDS.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w9-def-source">
                  {t('payroll.defs.create.source')}
                </label>
                <select
                  className="wp-field__input"
                  id="w9-def-source"
                  data-testid="w9-def-source"
                  value={defSource}
                  onChange={(event) => {
                    setDefSource(event.target.value as PayrollComponentSource);
                  }}
                >
                  {PAYROLL_COMPONENT_SOURCES.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w9-def-formula">
                  {t('payroll.defs.create.formula')}
                </label>
                <input
                  className="wp-field__input"
                  id="w9-def-formula"
                  data-testid="w9-def-formula"
                  type="text"
                  value={defFormula}
                  autoComplete="off"
                  onChange={(event) => {
                    setDefFormula(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w9-def-sort-order">
                  {t('payroll.defs.create.sortOrder')}
                </label>
                <input
                  className="wp-field__input"
                  id="w9-def-sort-order"
                  data-testid="w9-def-sort-order"
                  type="number"
                  min="0"
                  max="10000"
                  value={defSortOrder}
                  onChange={(event) => {
                    setDefSortOrder(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__actions">
                <button
                  className="wp-button wp-button--primary"
                  type="submit"
                  data-testid="w9-def-submit"
                  disabled={!defReady || props.defCreatePending}
                >
                  {props.defCreatePending
                    ? t('payroll.defs.create.submitting')
                    : t('payroll.defs.create.submit')}
                </button>
              </div>
            </form>
            {props.defCreateError === null ? null : (
              <ApiErrorNote
                error={props.defCreateError}
                subject={t('payroll.defs.subject')}
                testId="w9-def-create-error"
              />
            )}
            {props.defCreated === null ? null : (
              <StatusNote
                severity="low"
                title={t('payroll.defs.create.ok', { name: props.defCreated.name })}
                testId="w9-def-create-ok"
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}
